import { AiAssistantIcon } from '@/assets/static/icons/ai-assistant';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import FileNotSupported from '@/components/dueDiligence-page/modals/fileNotSupported';
import Label from '@/components/basicComponents/label';
import { useErrorMessages } from '@/hooks/i18n';
import { IFieldEditorAttachment } from '@/shared/types/fieldEditor';
import { updateMarkdownImageUrls } from '@/shared/utils/fieldEditor';
import { themeVar } from '@/styles/theme';
import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  CreateLink,
  diffSourcePlugin,
  DiffSourceToggleWrapper,
  headingsPlugin,
  imagePlugin,
  InsertTable,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  ListsToggle,
  MDXEditor,
  MDXEditorMethods,
  Separator,
  tablePlugin,
  thematicBreakPlugin,
  toolbarPlugin
} from '@mdxeditor/editor';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { useFieldEditorContext } from '../../context/FieldEditorContext';
import { getUnusedAttachmentsFromMarkdown } from '../../utils';
import FieldEditorContentWrapper from '../FieldEditorContentWrapper';
import ImageUpload from '../modals/imageUpload';

const LongFormContent = () => {
  const { default: defaultErrorMessage } = useErrorMessages();
  const { currentField, handleUpdateFieldAnswer, mainQueryRefetch, handleDeleteAttachment, handleUploadAttachment, handleClose } =
    useFieldEditorContext();
  const { control, reset, watch, setValue, handleSubmit } = useForm({ defaultValues: { fieldValue: '' } });

  const [loading, setLoading] = useState(false);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: 'not-supported' });
  const [error, setError] = useState<{ title: string; message: string } | null>(null);
  const [attachments, setAttachments] = useState<IFieldEditorAttachment[]>([]);
  const [isEditMode, setIsEditMode] = useState(true);

  const markdownEditorRef = useRef<MDXEditorMethods>(null);

  useEffect(() => {
    if (!currentField) return;

    if (currentField.value === null && currentField.AI?.answers?.length) {
      setIsEditMode(false);
      reset({ fieldValue: currentField.AI.answers[0]?.replace(/[<>]/g, '') || '' });
      if (markdownEditorRef?.current) {
        markdownEditorRef.current.setMarkdown(currentField.AI.answers[0]?.replace(/[<>]/g, '') || '');
      }
    } else {
      setIsEditMode(true);
      const markdownWithUpdatedImageUrls = updateMarkdownImageUrls(currentField.value || '', currentField?.attachments || []);
      reset({ fieldValue: markdownWithUpdatedImageUrls || '' });
      if (markdownEditorRef?.current) {
        markdownEditorRef.current.setMarkdown(markdownWithUpdatedImageUrls || '');
      }
    }

    setAttachments(currentField?.attachments || []);
  }, [currentField]);

  const saveValue = async (data: { fieldValue: string }) => {
    try {
      setError(null);
      setLoading(true);

      const unusedAttachments = getUnusedAttachmentsFromMarkdown({ markdown: data.fieldValue, attachments: attachments || [] });

      if (unusedAttachments?.length) {
        for await (const item of unusedAttachments) {
          await handleDeleteAttachment({ id: item.id });
        }
      }

      await handleUpdateFieldAnswer({ id: currentField.fieldAnswerId, recordId: currentField.recordId, value: isEditMode ? data.fieldValue : null });

      await mainQueryRefetch();
      handleClose();
    } catch (error) {
      setError({ title: 'Error', message: defaultErrorMessage });
    } finally {
      setLoading(false);
    }
  };

  const editSuggested = (suggestion: string) => {
    const formattedSuggestion = suggestion.replace(/[<>]/g, '');
    if (markdownEditorRef?.current) {
      markdownEditorRef.current.setMarkdown(formattedSuggestion);
    }
    setValue('fieldValue', formattedSuggestion);
    setIsEditMode(true);
  };

  const clearSuggestion = () => {
    setValue('fieldValue', currentField?.AI?.answers?.length ? '' : null);
    setIsEditMode(true);
    if (markdownEditorRef?.current) {
      markdownEditorRef.current.setMarkdown('');
    }
  };

  const startWithAISuggestion = () => {
    setValue('fieldValue', currentField.AI?.answers[0]?.replace(/[<>]/g, '') || '');
    if (markdownEditorRef?.current) {
      markdownEditorRef.current.setMarkdown(currentField.AI?.answers[0]?.replace(/[<>]/g, '') || '' || '');
    }
    setIsEditMode(false);
  };

  const revertPriorDraft = () => {
    if (currentField.value) {
      const markdownWithUpdatedImageUrls = updateMarkdownImageUrls(currentField.value || '', attachments || []);

      if (markdownEditorRef?.current) {
        markdownEditorRef.current.setMarkdown(markdownWithUpdatedImageUrls);
      }
      setValue('fieldValue', markdownWithUpdatedImageUrls);
      setIsEditMode(true);
    }
  };

  const updateLocalAttachments = ({ id, url, assetKey }: { id: string; url: string; assetKey: string }) => {
    setAttachments([...attachments, { id, assetKey, url }]);
  };

  if (!currentField) return <></>;

  return (
    <>
      {loading && (
        <ModalWrappedContent isOpen={loading} title="Saving" showCancelButton={false} showCloseIcon={false} onClose={() => {}}>
          <ModalLoader />
        </ModalWrappedContent>
      )}
      {modalWindow.type === 'not-supported' && (
        <FileNotSupported isOpen={modalWindow.isOpen} onClose={() => setModalWindow({ ...modalWindow, isOpen: false })} />
      )}
      <FieldEditorContentWrapper
        isEditMode={isEditMode}
        showRevertPriorDraft={Boolean(currentField.value) && currentField.value !== watch('fieldValue')}
        error={error}
        confirmButton={{ name: 'Save', onClick: handleSubmit(saveValue) }}
        startWithAISuggestion={startWithAISuggestion}
        editSuggested={editSuggested}
        clearSuggestion={clearSuggestion}
        revertPriorDraft={revertPriorDraft}
      >
        <div>
          <Label size="md">{currentField && currentField.name}</Label>
          <MDXEditorWrapper>
            <Controller
              name="fieldValue"
              control={control}
              render={({ field: { value, onChange } }) => (
                <CustomMDXEditor
                  isMagicWandStyleType={!isEditMode}
                  markdown={value || ''}
                  onChange={onChange}
                  ref={markdownEditorRef}
                  suppressHtmlProcessing={true}
                  readOnly={!isEditMode}
                  onError={(error) => console.log(error)}
                  plugins={[
                    listsPlugin(),
                    headingsPlugin(),
                    linkPlugin(),
                    linkDialogPlugin(),
                    tablePlugin(),
                    thematicBreakPlugin(),
                    imagePlugin({
                      disableImageSettingsButton: true
                    }),
                    diffSourcePlugin({ viewMode: 'rich-text' }),
                    toolbarPlugin({
                      toolbarContents: () => (
                        <DiffSourceToggleWrapper options={['rich-text', 'source']}>
                          <BoldItalicUnderlineToggles />
                          <CreateLink />
                          <Separator />
                          <ListsToggle options={['bullet', 'number']} />
                          <Separator />
                          <BlockTypeSelect />
                          <Separator />
                          <InsertTable />
                          <ImageUpload
                            recordId={currentField.recordId}
                            fieldId={currentField.id}
                            fieldAnswerId={currentField.fieldAnswerId}
                            markdownEditorRef={markdownEditorRef}
                            handleUploadAttachment={handleUploadAttachment}
                            updateLocalAttachments={updateLocalAttachments}
                          />
                        </DiffSourceToggleWrapper>
                      )
                    })
                  ]}
                />
              )}
            />
            {!isEditMode && (
              <MagicWandIconWrapper>
                <AiAssistantIcon width={28} height={28} fill={themeVar('font.weak')} />
              </MagicWandIconWrapper>
            )}
          </MDXEditorWrapper>
        </div>
      </FieldEditorContentWrapper>
    </>
  );
};

export default LongFormContent;

const MDXEditorWrapper = styled.div`
  position: relative;
`;

const CustomMDXEditor = styled(MDXEditor)<{ isMagicWandStyleType: boolean }>`
  border: 1px solid ${themeVar('border.base')};
  border-radius: 7px;
  font-family: Blinker;
  font-size: 16px;

  .mdxeditor-toolbar {
    top: 59px;
  }

  &.mdxeditor-popup-container {
    z-index: 1000;
  }

  .mdxeditor-root-contenteditable > div {
    cursor: text;
    min-height: 240px;
    max-height: 600px;
    overflow: auto;
  }

  .mdxeditor-root-contenteditable ul,
  .mdxeditor-root-contenteditable ol {
    padding-left: 26px;
  }

  .mdxeditor-root-contenteditable img {
    max-width: 100%;
    height: auto;
  }

  .mdxeditor-source-editor > div {
    cursor: text;
    min-height: 240px;
    max-height: 600px;
    overflow: auto;
  }

  .cm-editor {
    border-radius: 7px;
  }

  ${({ isMagicWandStyleType }) =>
    isMagicWandStyleType &&
    css`
      background-color: ${themeVar('action.disabled')};
      color: ${themeVar('font.base')};
      padding-left: 48px;

      .mdxeditor-toolbar {
        display: none;
      }
    `}
`;

const MagicWandIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 10px;
  height: 100%;
  padding-right: 10px;
  border-right: 1px solid ${themeVar('border.base')};
`;
